import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  MonitorSmartphoneIcon,
  SmartphoneIcon,
  TabletIcon,
  LaptopIcon,
  MonitorIcon,
  MousePointerIcon,
  PointerIcon,
  Moon,
  MousePointerClickIcon,
  ChevronRight,
  ArrowLeftIcon,
  PlusIcon,
  CheckIcon,
} from "lucide-react";
import { ResetIcon } from "@radix-ui/react-icons";
import { Separator } from "@/components/ui/separator";
import type { ValueByVariant } from "@/components/RightStyle/usePropertyStateV2";
import type { DropdownOptionType } from "../input";
import { cn } from "@/lib/utils";
import ColorSwatch from "../input/ColorSwatch";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  ALIGN_CONTENT_OPTIONS,
  JUSTIFY_CONTENT_OPTIONS,
  TEXT_ALIGN_OPTIONS,
  TEXT_DECORATION_LINE_OPTIONS,
} from "./variantOptions";

export type VariantType =
  | "default"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "hover"
  | "active"
  | "focus"
  | "dark";

interface VariantContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  valueByVariant: ValueByVariant;
  onSelectVariant?: (variant: VariantType) => void;
  onClearVariant?: (variant?: VariantType) => void;
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  // Enhanced features from VariantControl
  children?: (
    variant: VariantType | null,
    expanded: boolean,
    setExpanded: (v: boolean) => void,
  ) => React.ReactNode;
  onVariantChange?: (variant: VariantType, value: string | number) => void;
  onClearAllVariants?: () => void;
  controlType?:
    | "align"
    | "align-content"
    | "justify-content"
    | "direction-wrap"
    | "text-align"
    | "text-decoration-line"
    | "text-color";
  currentFlexDirection?: string;
  currentJustifyContent?: string;
  currentAlignItems?: string;
  // Control rendering mode
  usePortal?: boolean; // Whether to use portal for positioning (default: true)
}

const breakpointVariants = [
  {
    variant: "default",
    label: "default",
    desc: ">= 0rem (0px)",
    icon: <MonitorSmartphoneIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "sm",
    label: "sm",
    desc: ">= 40rem (640px)",
    icon: <SmartphoneIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "md",
    label: "md",
    desc: ">= 48rem (768px)",
    icon: <TabletIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "lg",
    label: "lg",
    desc: ">= 64rem (1024px)",
    icon: <LaptopIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "xl",
    label: "xl",
    desc: ">= 80rem (1280px)",
    icon: <MonitorIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "2xl",
    label: "2xl",
    desc: ">= 96rem (1536px)",
    icon: <MonitorIcon className="w-4 h-4 text-icons-primary" />,
  },
];

const interactionVariants = [
  {
    variant: "hover",
    label: "hover",
    desc: "apply on hover",
    icon: <MousePointerIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "active",
    label: "active",
    desc: "apply on active",
    icon: <PointerIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "focus",
    label: "focus",
    desc: "apply on focus",
    icon: <PointerIcon className="w-4 h-4 text-icons-primary" />,
  },
  {
    variant: "dark",
    label: "dark",
    desc: "apply in dark mode",
    icon: <Moon className="w-4 h-4 text-icons-primary" />,
  },
];

export const VariantDot = ({
  valueByVariant,
}: {
  valueByVariant: Record<
    string,
    { value: any; displayValue: string | null; active: boolean }
  >;
}) => {
  const variantsWithValues = Object.keys(valueByVariant).filter(
    (variant) =>
      valueByVariant[variant]?.value !== null &&
      valueByVariant[variant]?.value !== undefined,
  );
  const hasVariants = variantsWithValues.length >= 1;
  const activeCount = variantsWithValues.filter(
    (variant) => valueByVariant[variant].active,
  ).length;
  const onlyDefault =
    variantsWithValues.length === 1 && variantsWithValues[0] === "default";

  return (
    <div
      className={cn(
        "absolute right-[-3px] top-[-3px] bg-background-CTA size-[6px] rounded-full z-1000",
        hasVariants && activeCount === 0 && "bg-white",
        !hasVariants && "hidden",
        onlyDefault && "hidden",
      )}
    />
  );
};

// Helper functions from VariantControl
const justifyContentMap = ["flex-start", "center", "flex-end"];
const alignItemsMap = ["flex-start", "center", "flex-end"];

const modifyForReverse = (direction: string, alignment: string) => {
  if (direction?.endsWith("reverse")) {
    const reverseMap = {
      "flex-start": "flex-end",
      "flex-end": "flex-start",
      center: "center",
    };
    return reverseMap[alignment];
  }
  return alignment;
};

function getIndexFromStyles(
  direction: string,
  justifyContent: string,
  alignItems: string,
) {
  if (alignItems === "start") alignItems = "flex-start";
  else if (alignItems === "end") alignItems = "flex-end";
  if (justifyContent === "start") justifyContent = "flex-start";
  else if (justifyContent === "end") justifyContent = "flex-end";
  justifyContent = modifyForReverse(direction, justifyContent);
  const justifyContentIndex = justifyContentMap.indexOf(justifyContent);
  const alignItemsIndex = alignItemsMap.indexOf(alignItems);
  if (justifyContentIndex === -1 || alignItemsIndex === -1) return -1;
  let index = 0;
  if (!direction || direction.startsWith("row")) {
    index = alignItemsIndex * 3 + justifyContentIndex;
  } else {
    index = justifyContentIndex * 3 + alignItemsIndex;
  }
  return index;
}

const LayoutGraphicIcon = ({ index, row }: { index: number; row: boolean }) => {
  const rowStyles = "grow rounded-[1px] bg-icons-primary";
  let alignStyle = "";
  const justifyStyle = "";
  if (row) {
    if (index < 3) {
      alignStyle = "items-start";
    } else if (index < 6) {
      alignStyle = "items-center";
    } else {
      alignStyle = "items-end";
    }
  } else {
    if (index % 3 === 0) {
      alignStyle = "items-start";
    } else if (index % 3 === 1) {
      alignStyle = "items-center";
    } else {
      alignStyle = "items-end";
    }
  }
  return (
    <div
      className={cn(
        "flex h-5 w-5 gap-0.5 relative",
        row ? "flex-row" : "flex-col",
        alignStyle,
        justifyStyle,
      )}
    >
      <div className={cn(rowStyles, !row ? "w-7/12" : "h-4/6")} />
      <div className={cn(rowStyles, !row ? "w-full" : "h-full")} />
      <div className={cn(rowStyles, !row ? "w-5/12" : "h-2/6")} />
    </div>
  );
};

const getVariantIcons = (
  variantEntry: ValueByVariant[string],
  controlType: string,
  currentFlexDirection: string,
  currentJustifyContent: string,
  currentAlignItems: string,
) => {
  const icons: { [key: string]: React.ReactNode } = {};

  if (variantEntry?.value) {
    const optionsMap = {
      "align-content": ALIGN_CONTENT_OPTIONS,
      "justify-content": JUSTIFY_CONTENT_OPTIONS,
      "text-align": TEXT_ALIGN_OPTIONS,
      "text-decoration-line": TEXT_DECORATION_LINE_OPTIONS,
    };

    const options = optionsMap[controlType as keyof typeof optionsMap];
    if (options) {
      const found = options.find((opt) => opt.key === variantEntry.value);
      if (found) icons.custom = found.labelContent;
    }
  }

  if (controlType === "align") {
    const alignItems = variantEntry?.value || currentAlignItems;
    const index = getIndexFromStyles(
      currentFlexDirection,
      currentJustifyContent,
      alignItems,
    );
    if (index !== -1) {
      icons.align = (
        <LayoutGraphicIcon
          index={index}
          row={currentFlexDirection.startsWith("row")}
        />
      );
    }
  }

  return icons;
};

const getColorSwatchAndLabel = (
  variantEntry: ValueByVariant[string],
  controlType: string,
) => {
  if (controlType === "text-color" && variantEntry?.value) {
    const color = variantEntry.value;
    let resolvedColor: string | null = null;

    if (typeof window !== "undefined" && color.startsWith("text-")) {
      const tailwindClassesMap = useCodebaseStore.getState().tailwindClassesMap;

      if (tailwindClassesMap?.[color]?.css) {
        const css = tailwindClassesMap[color].css;
        const match = css.match(/color:\s*([^;]+);?/);
        if (match) resolvedColor = match[1].trim();
      } else {
        const arbitraryMatch = color.match(/^text-\[(.+)\]$/);
        if (arbitraryMatch) resolvedColor = arbitraryMatch[1];
      }
    }

    return {
      colorSwatch: <ColorSwatch color={resolvedColor || color} size="sm" />,
      valueLabel: "",
    };
  }

  if (
    [
      "direction-wrap",
      "gradient-direction",
      "from-position",
      "to-position",
      "via-position",
    ].includes(controlType ?? "") &&
    variantEntry?.value
  ) {
    return {
      colorSwatch: null,
      valueLabel: variantEntry.displayValue || variantEntry.value,
    };
  }

  return { colorSwatch: null, valueLabel: "" };
};

const VariantContextMenu: React.FC<VariantContextMenuProps> = ({
  isOpen,
  onClose,
  anchorRef,
  valueByVariant,
  onSelectVariant,
  onClearVariant,
  dropdownOptions = [],
  onDropdownSelected,
  children,
  onVariantChange,
  onClearAllVariants,
  controlType,
  currentFlexDirection = "",
  currentJustifyContent = "",
  currentAlignItems = "",
  usePortal = true,
}) => {
  const [menuMode, setMenuMode] = useState<
    | { mode: "main" }
    | { mode: "breakpoints" }
    | { mode: "interactions" }
    | {
        mode: "variant";
        variant: VariantType;
        from: "breakpoints" | "interactions";
      }
  >({ mode: "main" });
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState<{
    top: number | null;
    bottom: number | null;
    right: number | null;
  }>({ top: null, bottom: null, right: null });
  const menuRef = useRef<HTMLDivElement>(null);

  // Get portal root
  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);

  // Calculate position using original VariantControl logic
  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const menuHeight = 300;

      let top: number | null = null,
        bottom: number | null = null,
        right: number | null = null;
      // Always prefer positioning below the element
      top = rect.bottom + 4;

      // If it would go off the bottom of the viewport, adjust it
      if (top + menuHeight > window.innerHeight) {
        top = null;
        bottom = window.innerHeight - rect.top + 4;
      }
      right = window.innerWidth - rect.right - 4;

      setPosition({
        top: top ? Math.max(top, 8) : null,
        bottom: bottom ? Math.max(bottom, 8) : null,
        right: right ? Math.max(right, 8) : null,
      });
    }
  }, [isOpen, anchorRef]);

  // Reset menu mode when closing
  useEffect(() => {
    if (!isOpen) {
      setMenuMode({ mode: "main" });
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Use a slight delay to avoid immediate close from the opening click
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const breakpointCount = Object.keys(valueByVariant).filter(
    (v) =>
      ["sm", "md", "lg", "xl", "2xl"].includes(v) && valueByVariant[v]?.value,
  ).length;

  const interactionCount = Object.keys(valueByVariant).filter(
    (v) =>
      ["hover", "active", "focus", "dark"].includes(v) &&
      valueByVariant[v]?.value,
  ).length;

  const handleVariantSelect = useCallback(
    (variant: VariantType) => {
      if (
        children &&
        (menuMode.mode === "breakpoints" || menuMode.mode === "interactions")
      ) {
        // If we have children render prop, navigate to variant view
        setMenuMode({ mode: "variant", variant, from: menuMode.mode });
      } else {
        // Simple selection mode
        if (onSelectVariant) {
          onSelectVariant(variant);
        }
        onClose();
      }
    },
    [onSelectVariant, onClose, children, menuMode],
  );

  const handleMenuSelect = useCallback(
    (section: "breakpoints" | "interactions") => {
      setMenuMode({ mode: section });
    },
    [],
  );

  const handleBack = useCallback(() => {
    if (menuMode.mode === "main") {
      onClose();
    } else if (menuMode.mode === "variant") {
      setMenuMode({ mode: menuMode.from });
    } else {
      setMenuMode({ mode: "main" });
    }
  }, [menuMode, onClose]);

  const renderVariantList = (
    variants: typeof breakpointVariants,
    title: string,
  ) => {
    return (
      <>
        <div className="flex items-center gap-2 p-2 border-b">
          <button
            type="button"
            onClick={handleBack}
            className="p-1 hover:bg-secondary rounded"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <span className="text-xs font-medium">{title}</span>
        </div>
        <div className="p-2 flex flex-col gap-y-1">
          {variants.map((bp) => {
            const variantEntry = valueByVariant[bp.variant];
            const icons = getVariantIcons(
              variantEntry,
              controlType || "",
              currentFlexDirection,
              currentJustifyContent,
              currentAlignItems,
            );
            const { colorSwatch, valueLabel } = getColorSwatchAndLabel(
              variantEntry,
              controlType || "",
            );
            const hasValue =
              !!variantEntry &&
              (variantEntry.value || variantEntry.displayValue);

            return (
              <div
                key={bp.variant}
                className="px-2 py-1 flex items-center justify-between cursor-pointer hover:bg-secondary rounded-md"
                onClick={() => handleVariantSelect(bp.variant as VariantType)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleVariantSelect(bp.variant as VariantType);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center gap-2">
                  <div className="relative">
                    {bp.icon}
                    <VariantDot
                      valueByVariant={
                        variantEntry?.value !== null &&
                        variantEntry?.value !== undefined
                          ? {
                              [bp.variant]: variantEntry,
                            }
                          : {}
                      }
                    />
                  </div>
                  <span className="text-xs font-medium">{bp.label}</span>
                  <span className="text-xs text-secondary-n-500">
                    {bp.desc}
                  </span>
                </div>
                {controlType === "text-color" ? (
                  variantEntry?.value ? (
                    <span className="flex items-center gap-1">
                      {colorSwatch}
                      <span className="text-xs text-white mr-2">
                        {valueLabel}
                      </span>
                      <ChevronRight className="w-4 h-4 text-icons-primary" />
                    </span>
                  ) : (
                    <PlusIcon className="w-4 h-4 text-icons-primary" />
                  )
                ) : hasValue ? (
                  <span className="flex items-center gap-1">
                    {icons.direction}
                    {icons.wrap}
                    {icons.custom}
                    {icons.align}
                    <ChevronRight className="w-4 h-4 text-icons-primary" />
                  </span>
                ) : (
                  <PlusIcon className="w-4 h-4 text-icons-primary" />
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderVariantContent = () => {
    if (menuMode.mode !== "variant" || !children) return null;

    const variantObj =
      breakpointVariants.find((v) => v.variant === menuMode.variant) ||
      interactionVariants.find((v) => v.variant === menuMode.variant);

    return (
      <>
        <div className="flex items-center gap-2 p-2 border-b">
          <button
            type="button"
            onClick={handleBack}
            className="p-1 hover:bg-secondary rounded"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <span className="flex items-center gap-2">
            {variantObj?.icon}
            <span className="text-xs font-medium">
              {variantObj?.label || menuMode.variant}
            </span>
            <span className="text-xs text-secondary-n-500">
              {variantObj?.desc}
            </span>
          </span>
        </div>
        <div className="p-2">
          {children(menuMode.variant, true, () =>
            setMenuMode({ mode: "main" }),
          )}
        </div>
      </>
    );
  };

  if (!isOpen) return null;

  // For portal rendering, we need the portalRoot
  if (usePortal && !portalRoot) return null;
  const renderMenuContent = () => (
    <>
      {menuMode.mode === "main" && (
        <div className="p-2 flex flex-col gap-y-1">
          {/* Dropdown options with group support */}
          {dropdownOptions.length > 0 && (
            <>
              {dropdownOptions.map((option, index) => {
                const prevOption = dropdownOptions[index - 1];
                const showSeparator =
                  prevOption &&
                  prevOption.group !== undefined &&
                  option.group !== undefined &&
                  prevOption.group !== option.group;

                return (
                  <React.Fragment key={option.value}>
                    {showSeparator && <Separator />}
                    <div
                      className="px-2 py-1 flex items-center justify-between cursor-pointer hover:bg-accent rounded-sm"
                      onClick={() => {
                        if (onDropdownSelected) {
                          onDropdownSelected(option);
                        }
                        onClose();
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`${option.selected ? "" : "opacity-50"}`}
                        >
                          {option.icon}
                        </div>
                        <span className="text-xs">{option.label}</span>
                      </div>
                      {option.selected && <CheckIcon className="w-4 h-4" />}
                    </div>
                  </React.Fragment>
                );
              })}
              <Separator />
            </>
          )}

          {/* Breakpoints menu */}
          <div
            className="px-2 py-1 flex flex-row justify-between items-center cursor-pointer hover:bg-secondary rounded-md"
            onClick={() => handleMenuSelect("breakpoints")}
          >
            <span className="flex items-center gap-2">
              <div className="relative">
                <MonitorSmartphoneIcon className="w-4 h-4 text-icons-primary" />
                <VariantDot
                  valueByVariant={Object.fromEntries(
                    breakpointVariants
                      .filter(
                        (bp) => valueByVariant[bp.variant]?.value !== null,
                      )
                      .map((bp) => [bp.variant, valueByVariant[bp.variant]]),
                  )}
                />
              </div>
              <span>Responsive Breakpoints</span>
            </span>
            <div className="flex items-center gap-2">
              {breakpointCount > 0 && (
                <span className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent min-w-[15px] h-[16px] flex items-center justify-center">
                  {breakpointCount}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-icons-primary" />
            </div>
          </div>

          {/* Interaction states menu */}
          <div
            className="px-2 py-1 flex flex-row justify-between items-center cursor-pointer hover:bg-secondary rounded-md"
            onClick={() => handleMenuSelect("interactions")}
          >
            <span className="flex items-center gap-2">
              <div className="relative">
                <MousePointerClickIcon className="w-4 h-4 text-icons-primary" />
                <VariantDot
                  valueByVariant={Object.fromEntries(
                    interactionVariants
                      .filter(
                        (iv) =>
                          valueByVariant[iv.variant]?.value !== null &&
                          valueByVariant[iv.variant]?.value !== undefined,
                      )
                      .map((iv) => [iv.variant, valueByVariant[iv.variant]]),
                  )}
                />
              </div>
              <span>Interaction States</span>
            </span>
            <div className="flex items-center gap-2">
              {interactionCount > 0 && (
                <span className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent min-w-[15px] h-[16px] flex items-center justify-center">
                  {interactionCount}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-icons-primary" />
            </div>
          </div>

          {/* Clear style option */}
          {Object.values(valueByVariant).some((v) => v.value) && (
            <>
              <Separator />
              <div
                className="px-2 py-1 flex items-center gap-2 cursor-pointer hover:bg-secondary rounded-md"
                onClick={() => {
                  if (onClearAllVariants) {
                    onClearAllVariants();
                  } else if (onClearVariant) {
                    // Clear all variants individually if no onClearAllVariants provided
                    for (const variant of Object.keys(valueByVariant)) {
                      onClearVariant(variant as VariantType);
                    }
                  }
                  onClose();
                }}
              >
                <ResetIcon className="w-4 h-4" />
                <span>Clear style</span>
              </div>
            </>
          )}
        </div>
      )}

      {menuMode.mode === "breakpoints" &&
        renderVariantList(breakpointVariants, "Responsive Breakpoints")}

      {menuMode.mode === "interactions" &&
        renderVariantList(interactionVariants, "Interaction States")}

      {renderVariantContent()}
    </>
  );

  const content = usePortal ? (
    // Original VariantControl portal setup: fixed overlay + absolute modal
    <div
      data-inside-modal="true"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      style={{ position: "fixed", inset: 0, zIndex: 100001 }}
    >
      <div
        ref={menuRef}
        data-inside-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="absolute bg-popover shadow-md border rounded-md text-xs"
        style={{
          top: position.top ? `${position.top}px` : undefined,
          bottom: position.bottom ? `${position.bottom}px` : undefined,
          right: position.right ? `${position.right}px` : undefined,
          width: 250,
          zIndex: 100002,
          fontSize: "0.75rem",
        }}
      >
        {renderMenuContent()}
      </div>
    </div>
  ) : (
    // Non-portal version for inline usage
    <div
      ref={menuRef}
      className="bg-popover shadow-md border rounded-md text-xs w-[250px]"
      data-inside-modal="true"
    >
      {renderMenuContent()}
    </div>
  );

  // Return portal version or inline version based on usePortal prop
  return usePortal ? ReactDOM.createPortal(content, portalRoot!) : content;
};

export default React.memo(VariantContextMenu);
